import { CheckCircle2, Info } from 'lucide-react';
import { Badge } from '@/components/ui';
import type { PluginInstallReport } from '../../types';

interface ImportReportPanelProps {
  report: PluginInstallReport;
}

export default function ImportReportPanel({ report }: ImportReportPanelProps) {
  // Defensive defaults — backend payloads have varied across iterations
  // and a missing field shouldn't blank the screen.
  const resources = report.resources ?? [];
  const skipped = report.skipped ?? [];
  const mcpServersReported = report.mcpServersReported ?? [];

  const counts = resources.reduce(
    (acc, r) => {
      acc[r.action] = (acc[r.action] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const summary: string[] = [];
  if (counts.created) summary.push(`${counts.created} created`);
  if (counts.updated) summary.push(`${counts.updated} updated`);
  if (counts.unchanged) summary.push(`${counts.unchanged} unchanged`);
  if (counts.removed) summary.push(`${counts.removed} removed`);
  if (skipped.length) summary.push(`${skipped.length} skipped`);

  const grouped = {
    skills: resources.filter((r) => r.entityType === 'skill'),
    agents: resources.filter((r) => r.entityType === 'agent'),
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-md border border-status-success-bg bg-status-success-bg/30 p-3">
        <CheckCircle2 className="h-5 w-5 text-status-success-fg mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">Resources imported</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {summary.join(' · ') || 'Nothing changed.'}
          </p>
        </div>
      </div>

      {grouped.skills.length > 0 && (
        <ResourceSection title={`Skills (${grouped.skills.length})`} resources={grouped.skills} />
      )}
      {grouped.agents.length > 0 && (
        <ResourceSection title={`Agents (${grouped.agents.length})`} resources={grouped.agents} />
      )}

      {skipped.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">Skipped</h3>
          <ul className="space-y-1">
            {skipped.map((s) => (
              <li
                key={s.path}
                className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
              >
                <div className="font-mono text-foreground">{s.path}</div>
                <div className="text-muted-foreground mt-0.5">
                  <span className="font-medium">{s.resourceType}</span> — {s.reason}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {mcpServersReported.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
            <Info className="h-3.5 w-3.5 mt-0.5 text-muted-foreground" />
            <p className="text-muted-foreground">
              {mcpServersReported.length} MCP server
              {mcpServersReported.length === 1 ? '' : 's'} reported. Wire these manually via
              Tools → MCP servers.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

function ResourceSection({
  title,
  resources,
}: {
  title: string;
  resources: PluginInstallReport['resources'];
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      <ul className="space-y-1">
        {resources.map((r) => (
          <li
            key={r.resourceKey}
            className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-xs"
          >
            <span className="font-mono text-foreground">{r.resourceKey}</span>
            <Badge variant={actionVariant(r.action)}>{r.action}</Badge>
          </li>
        ))}
      </ul>
    </section>
  );
}

function actionVariant(action: string) {
  switch (action) {
    case 'created':
      return 'success' as const;
    case 'updated':
      return 'info' as const;
    case 'removed':
      return 'error' as const;
    default:
      return 'gray' as const;
  }
}
